import { useEffect, useState } from "react";

import type { ShopperOrder } from "@sirdab/returns-shared";
import { Button, cn, Skeleton } from "@sirdab/ui";

import { useApi, type ApiFailure } from "../../api";
import { ErrorState, Money } from "../../components";
import { useFlow } from "../../flow";
import { formatCount, formatDate } from "../../format/money";
import { useMessages } from "../../i18n";
import { useFailureText } from "../shared";
import { StepScaffold } from "../StepScaffold";

/**
 * The proven phone's orders, newest first. Every order is LISTED, returnable or not
 * (ADR-007): a row that vanished here would only generate a support message. One that
 * cannot start a return is shown greyed, carrying the server's own reason, and cannot be
 * picked — which is the difference between "not for you" and a dead end.
 */
export function OrdersStep({
  shopperToken,
  onPick,
  onHaveOrderRef,
}: {
  shopperToken: string;
  onPick: (orderRef: string) => Promise<void>;
  onHaveOrderRef: () => void;
}): React.JSX.Element {
  const api = useApi();
  const { t, text, locale } = useMessages();
  const { merchantSlug } = useFlow();
  const failureText = useFailureText();

  const [orders, setOrders] = useState<ShopperOrder[] | null>(null);
  const [failure, setFailure] = useState<ApiFailure | null>(null);
  const [picking, setPicking] = useState<string | null>(null);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setFailure(null);
    setOrders(null);
    void api
      .listShopperOrders({ merchantSlug }, shopperToken)
      .then((result) => {
        if (cancelled) return;
        if (!result.ok) {
          setFailure(result);
          return;
        }
        setOrders(result.data.orders);
      });
    return () => {
      cancelled = true;
    };
  }, [api, merchantSlug, shopperToken, attempt]);

  async function pick(orderRef: string): Promise<void> {
    setFailure(null);
    setPicking(orderRef);
    await onPick(orderRef);
    setPicking(null);
  }

  return (
    <StepScaffold
      step="lookup"
      titleKey="screen.lookup.orders_title"
      descriptionKey="screen.lookup.orders_description"
    >
      <div className="flex flex-col gap-4">
        {failure !== null ? (
          <ErrorState
            message={failureText(failure)}
            retryLabel={t("action.retry")}
            onRetry={() => setAttempt((n) => n + 1)}
          />
        ) : orders === null ? (
          <div className="flex flex-col gap-2" aria-busy="true">
            <Skeleton className="h-control-touch w-full" />
            <Skeleton className="h-control-touch w-full" />
          </div>
        ) : orders.length === 0 ? (
          <p className="text-body text-subtle">{t("shopper.orders.empty")}</p>
        ) : (
          <ul className="flex flex-col gap-2">
            {orders.map((order) => (
              <li key={order.orderRef}>
                <button
                  type="button"
                  disabled={picking !== null || !order.returnable}
                  aria-busy={picking === order.orderRef}
                  onClick={() => void pick(order.orderRef)}
                  className={cn(
                    "min-h-control-touch flex w-full flex-col gap-1 rounded-lg border p-3 text-start",
                    "border-border focus-visible:ring-ring focus-visible:ring-2 focus-visible:outline-none",
                    order.returnable
                      ? "hover:border-foreground"
                      : "cursor-not-allowed",
                    "disabled:opacity-50",
                  )}
                >
                  <span className="flex w-full items-baseline justify-between gap-3">
                    <span className="text-body text-foreground">
                      <span dir="ltr" className="tabular-nums">
                        #{order.orderRef}
                      </span>
                    </span>
                    <Money halalas={order.totalHalalas} />
                  </span>
                  <span className="text-caption-quiet text-subtle flex flex-wrap items-baseline gap-x-2">
                    <span>{text(order.statusLabel)}</span>
                    {order.placedAt !== null ? (
                      <span>
                        {t("shopper.orders.placed")}{" "}
                        {formatDate(order.placedAt, locale)}
                      </span>
                    ) : null}
                    {order.itemCount !== null ? (
                      <span>
                        {formatCount(order.itemCount, locale)}{" "}
                        {t("shopper.orders.items")}
                      </span>
                    ) : null}
                  </span>
                  {order.itemNames.length > 0 ? (
                    <span className="text-caption-quiet text-subtle">
                      {order.itemNames.map((name) => text(name)).join(" · ")}
                    </span>
                  ) : null}
                  {/* Server-authored, rendered as-is: the portal never writes a policy
                      refusal in its own words. */}
                  {order.blockedReason !== null ? (
                    <span className="text-caption text-foreground">
                      {text(order.blockedReason)}
                    </span>
                  ) : null}
                </button>
              </li>
            ))}
          </ul>
        )}

        <Button
          type="button"
          variant="link"
          size="xl"
          onClick={onHaveOrderRef}
          className="self-center"
        >
          {t("action.have_order_ref")}
        </Button>
      </div>
    </StepScaffold>
  );
}
